import { ArrowRight, Check, ChevronLeft, ChevronRight, ExternalLink, MapPin, Sparkles, Store, Tag, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import { getUntaggedEstablishments, updateEstablishmentTags, getEstablishmentTags } from "../../../interfaces/establishments";
import { IEstablishment } from "../../../interfaces/establishment";

type Period = "week" | "month" | "all";

interface TagInputProps {
    label: string;
    value: string[];
    suggestions: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    color: "blue" | "purple";
}

function TagInput({ label, value, suggestions, onChange, placeholder, color }: TagInputProps) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const filtered = query.length > 0
        ? suggestions.filter(s => s.toLowerCase().includes(query.toLowerCase()) && !value.includes(s))
        : suggestions.filter(s => !value.includes(s)).slice(0, 8);

    const add = (tag: string) => {
        const trimmed = tag.trim();
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setQuery("");
        inputRef.current?.focus();
    };

    const remove = (tag: string) => onChange(value.filter(t => t !== tag));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === "Enter" || e.key === ",") && query.trim()) {
            e.preventDefault();
            add(query);
        }
        if (e.key === "Backspace" && !query && value.length > 0) {
            remove(value[value.length - 1]);
        }
        if (e.key === "Escape") setOpen(false);
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const tagColors = color === "blue"
        ? "bg-gray-100 text-gray-700 border-gray-200"
        : "bg-purple-50 text-purple-700 border-purple-200";

    const dotColor = color === "blue" ? "bg-gray-100" : "bg-purple-500";

    return (
        <div ref={containerRef} className="relative">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                {label}
            </p>
            <div
                className="min-h-[38px] flex flex-wrap gap-1.5 items-center border border-gray-200 rounded-sm p-1.5 cursor-text bg-white focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-200 transition-all"
                onClick={() => { inputRef.current?.focus(); setOpen(true); }}
            >
                {value.map(tag => (
                    <span
                        key={tag}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium border ${tagColors}`}
                    >
                        {tag}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); remove(tag); }}
                            className="hover:opacity-70 transition-opacity"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? placeholder : ""}
                    className="flex-1 min-w-[120px] border-0 outline-none focus:ring-0 text-xs text-gray-700 placeholder:text-gray-400 bg-transparent"
                />
            </div>

            {open && filtered.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-sm max-h-48 overflow-y-auto">
                    {filtered.map(s => (
                        <button
                            key={s}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); add(s); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2"
                        >
                            <Tag className="w-3 h-3 text-gray-400 shrink-0" />
                            {s}
                        </button>
                    ))}
                    {query.trim() && !suggestions.includes(query.trim()) && (
                        <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); add(query); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2 border-t border-gray-100"
                        >
                            <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
                            Create &ldquo;<strong>{query.trim()}</strong>&rdquo;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

interface EstablishmentCardProps {
    establishment: IEstablishment;
    allCategories: string[];
    allSubCategories: string[];
    onSaved: (id: string) => void;
    onSkip: (id: string) => void;
}

function EstablishmentCard({ establishment, allCategories, allSubCategories, onSaved, onSkip }: EstablishmentCardProps) {
    const [categories, setCategories] = useState<string[]>(establishment.categories || []);
    const [subCategories, setSubCategories] = useState<string[]>(establishment.subCategories || []);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const handleSave = async () => {
        if (categories.length === 0 && subCategories.length === 0) return;
        setSaving(true);
        try {
            await updateEstablishmentTags(establishment._id, { categories, subCategories });
            setSaved(true);
            setTimeout(() => onSaved(establishment._id), 600);
        } catch (err) {
            console.error("Failed to save tags", err);
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = categories.length > 0 || subCategories.length > 0;
    const city = establishment.address?.city;
    const postcode = establishment.address?.postcode;
    const location = [postcode, city].filter(Boolean).join(" ");

    const mapsUrl = establishment.ratings?.source_summary?.find((s: any) => s.source === "google")?.url ?? null;
    const siren = establishment.registration?.siren;
    const pappersUrl = siren ? `https://www.pappers.fr/entreprise/${siren}` : null;

    const createdDate = establishment.createdAt
        ? new Date(establishment.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
        : null;

    return (
        <div className={`bg-white border rounded-sm transition-all duration-300 ${saved ? "border-green-300 bg-green-50/30 opacity-60" : "border-gray-200 hover:border-gray-300"}`}>
            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-sm bg-gray-100 flex items-center justify-center shrink-0">
                            <Store className="w-4.5 h-4.5 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 truncate leading-tight">
                                {establishment.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {location && (
                                    <span className="text-[11px] text-gray-500">{location}</span>
                                )}
                                {establishment.type && (
                                    <span className="px-1.5 py-0.5 rounded-sm bg-gray-100 text-[10px] font-medium text-gray-600 border border-gray-200">
                                        {establishment.type}
                                    </span>
                                )}
                                {establishment.brand_group && (
                                    <span className="px-1.5 py-0.5 rounded-sm bg-gray-100 text-[10px] font-medium text-gray-700 border border-gray-200">
                                        {establishment.brand_group}
                                    </span>
                                )}
                            </div>
                            {establishment.primary_declared_activity && (
                                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                                    {establishment.primary_declared_activity}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                        {mapsUrl && (
                            <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Voir sur Google Maps"
                                className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                                <MapPin className="w-3.5 h-3.5" />
                            </a>
                        )}
                        {pappersUrl && (
                            <a
                                href={pappersUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Voir sur Pappers"
                                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-[#333] transition-colors"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        )}
                        {createdDate && (
                            <span className="text-[10px] text-gray-400 pl-1">{createdDate}</span>
                        )}
                    </div>
                </div>

                {/* Tag Inputs */}
                <div className="space-y-3">
                    <TagInput
                        label="Catégories"
                        value={categories}
                        suggestions={allCategories}
                        onChange={setCategories}
                        placeholder="Ex: Restaurant, Bar, Café..."
                        color="blue"
                    />
                    <TagInput
                        label="Sous-catégories"
                        value={subCategories}
                        suggestions={allSubCategories}
                        onChange={setSubCategories}
                        placeholder="Ex: Japonais, Brunch, Tapas..."
                        color="purple"
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <button
                        onClick={() => onSkip(establishment._id)}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1.5 rounded-sm hover:bg-[#F9F6F3]"
                    >
                        <ArrowRight className="w-3.5 h-3.5" />
                        Ignorer
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges || saved}
                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-sm text-xs font-medium transition-all ${
                            saved
                                ? "bg-green-500 text-white"
                                : hasChanges
                                ? "bg-black text-white hover:bg-gray-800"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                        {saved ? (
                            <>
                                <Check className="w-3.5 h-3.5" />
                                Enregistré
                            </>
                        ) : saving ? (
                            <>
                                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Sauvegarde...
                            </>
                        ) : (
                            <>
                                <Check className="w-3.5 h-3.5" />
                                Enregistrer
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

const PERIOD_OPTIONS: { label: string; value: Period; desc: string }[] = [
    { label: "Cette semaine", value: "week", desc: "Ajoutés ces 7 derniers jours" },
    { label: "Ce mois", value: "month", desc: "Ajoutés ces 30 derniers jours" },
    { label: "Tout", value: "all", desc: "Tous les établissements" },
];

export default function TodoTags() {
    const [period, setPeriod] = useState<Period>("all");
    const [sanitaryOnly, setSanitaryOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [establishments, setEstablishments] = useState<IEstablishment[]>([]);
    const [stats, setStats] = useState<{ total: number; totalUntagged: number; totalAll: number; totalPages: number } | null>(null);
    const [allCategories, setAllCategories] = useState<string[]>([]);
    const [allSubCategories, setAllSubCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [skipped, setSkipped] = useState<Set<string>>(new Set());

    const LIMIT = 12;

    const fetchData = useCallback(async (p: Period, pg: number, sanitary: boolean) => {
        setLoading(true);
        try {
            const data = await getUntaggedEstablishments({
                period: p,
                page: pg,
                limit: LIMIT,
                ...(sanitary ? { signalType: "hygiene_control" } : {}),
            });
            setEstablishments(data.establishments);
            setStats({
                total: data.total,
                totalUntagged: data.totalUntagged,
                totalAll: data.totalAll,
                totalPages: data.totalPages,
            });
        } catch (err) {
            console.error("Failed to fetch untagged establishments", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTags = useCallback(async () => {
        try {
            const tags = await getEstablishmentTags();
            setAllCategories(tags.categories);
            setAllSubCategories(tags.subCategories);
        } catch (err) {
            console.error("Failed to fetch tags", err);
        }
    }, []);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    useEffect(() => {
        setPage(1);
        setSkipped(new Set());
        fetchData(period, 1, sanitaryOnly);
    }, [period, sanitaryOnly, fetchData]);

    const handleSaved = (id: string) => {
        setEstablishments(prev => prev.filter(e => e._id !== id));
        if (stats) {
            setStats(prev => prev ? {
                ...prev,
                totalUntagged: Math.max(0, prev.totalUntagged - 1),
                total: Math.max(0, prev.total - 1),
            } : prev);
        }
        // Refresh tags in background (new tag might have been created)
        fetchTags();
    };

    const handleSkip = (id: string) => {
        setSkipped(prev => new Set([...prev, id]));
        setEstablishments(prev => prev.filter(e => e._id !== id));
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setSkipped(new Set());
        fetchData(period, newPage, sanitaryOnly);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const visibleEstablishments = establishments.filter(e => !skipped.has(e._id));

    const tagged = stats ? stats.totalAll - stats.totalUntagged : 0;
    const progress = stats && stats.totalAll > 0 ? (tagged / stats.totalAll) * 100 : 0;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">Todo Tags</h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Taguer les établissements sans catégories ni sous-catégories
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <BONavigation />

                {/* Progress Section */}
                {stats && (
                    <div className="mb-6 bg-white border border-gray-200 rounded-sm p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-semibold text-gray-900">Progression globale</span>
                            </div>
                            <div className="text-right">
                                <span className="text-2xl font-semibold text-gray-900">{progress.toFixed(1)}%</span>
                                <p className="text-[11px] text-gray-500">
                                    <span className="font-medium text-gray-700">{tagged.toLocaleString("fr-FR")}</span> tagués sur{" "}
                                    <span className="font-medium text-gray-700">{stats.totalAll.toLocaleString("fr-FR")}</span>
                                </p>
                            </div>
                        </div>
                        {/* Progress bar */}
                        <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-700 ease-out"
                                style={{
                                    width: `${progress}%`,
                                    background: progress >= 80
                                        ? "linear-gradient(90deg, #22c55e, #16a34a)"
                                        : progress >= 50
                                        ? "linear-gradient(90deg, #3b82f6, #6366f1)"
                                        : "linear-gradient(90deg, #f59e0b, #ef4444)",
                                }}
                            />
                            {progress > 5 && (
                                <div
                                    className="absolute top-0 h-full"
                                    style={{ left: `calc(${progress}% - 2px)`, width: "2px", background: "rgba(255,255,255,0.6)" }}
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                            <p className="text-[11px] text-gray-500">
                                <span className="font-semibold text-red-600">{stats.totalUntagged.toLocaleString("fr-FR")}</span> restants sans tags
                            </p>
                            <p className="text-[11px] text-gray-500">
                                {stats.totalAll.toLocaleString("fr-FR")} établissements au total
                            </p>
                        </div>
                    </div>
                )}

                {/* Period Filter */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 flex-wrap">
                        {PERIOD_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setPeriod(opt.value)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all border ${
                                    period === opt.value
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900"
                                }`}
                                title={opt.desc}
                            >
                                {opt.label}
                                {stats && period === opt.value && (
                                    <span className={`text-xs rounded-sm px-1.5 py-0.5 font-semibold ${
                                        period === opt.value ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                                    }`}>
                                        {stats.total}
                                    </span>
                                )}
                            </button>
                        ))}
                        <div className="w-px h-5 bg-gray-200 mx-1" />
                        <button
                            onClick={() => setSanitaryOnly(v => !v)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all border ${
                                sanitaryOnly
                                    ? "bg-red-600 text-white border-red-600"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900"
                            }`}
                            title="Afficher uniquement les établissements ayant un contrôle sanitaire"
                        >
                            🏥 Contrôles sanitaires
                        </button>
                        {stats && (
                            <span className="ml-auto text-xs text-gray-500">
                                {stats.total} établissements · page {page} / {stats.totalPages}
                            </span>
                        )}
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-sm p-4 animate-pulse">
                                <div className="flex gap-3 mb-4">
                                    <div className="w-9 h-9 bg-gray-100 rounded-sm shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-100 rounded w-3/4" />
                                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="h-9 bg-gray-100 rounded-sm" />
                                    <div className="h-9 bg-gray-100 rounded-sm" />
                                </div>
                                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                                    <div className="h-6 w-16 bg-gray-100 rounded" />
                                    <div className="h-6 w-24 bg-gray-100 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : visibleEstablishments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <Check className="w-8 h-8 text-green-500" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">
                            {skipped.size > 0 ? "Tous ignorés pour cette page" : "Tout est tagué !"}
                        </h3>
                        <p className="text-sm text-gray-500 mb-6 max-w-sm">
                            {skipped.size > 0
                                ? "Vous avez ignoré tous les établissements de cette page."
                                : "Il n'y a plus d'établissements sans tags pour cette période."}
                        </p>
                        {skipped.size > 0 && stats && page < stats.totalPages && (
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-sm hover:bg-gray-800 transition-colors"
                            >
                                Page suivante
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {visibleEstablishments.map(est => (
                                <EstablishmentCard
                                    key={est._id}
                                    establishment={est}
                                    allCategories={allCategories}
                                    allSubCategories={allSubCategories}
                                    onSaved={handleSaved}
                                    onSkip={handleSkip}
                                />
                            ))}
                        </div>

                        {/* Pagination */}
                        {stats && stats.totalPages > 1 && (
                            <div className="flex items-center justify-center gap-3 mt-8 pb-8">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-sm text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Précédent
                                </button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(stats.totalPages, 7) }).map((_, i) => {
                                        let p: number;
                                        if (stats.totalPages <= 7) {
                                            p = i + 1;
                                        } else if (page <= 4) {
                                            if (i < 5) p = i + 1;
                                            else if (i === 5) p = -1; // ellipsis
                                            else p = stats.totalPages;
                                        } else if (page >= stats.totalPages - 3) {
                                            if (i === 0) p = 1;
                                            else if (i === 1) p = -1;
                                            else p = stats.totalPages - (6 - i);
                                        } else {
                                            if (i === 0) p = 1;
                                            else if (i === 1) p = -1;
                                            else if (i >= 2 && i <= 4) p = page + (i - 3);
                                            else if (i === 5) p = -2;
                                            else p = stats.totalPages;
                                        }
                                        if (p < 0) return (
                                            <span key={`ellipsis-${i}`} className="w-8 text-center text-gray-400 text-sm">…</span>
                                        );
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => handlePageChange(p)}
                                                className={`w-8 h-8 rounded-sm text-sm font-medium transition-colors ${
                                                    p === page
                                                        ? "bg-black text-white"
                                                        : "text-gray-600 hover:bg-gray-100"
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= stats.totalPages}
                                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-sm text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    Suivant
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
